<template>
  <Teleport to="body">
    <Transition
      appear
      enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      @after-enter="$emit('opened')"
      @after-leave="$emit('closed')"
    >
      <div v-if="show" class="fixed inset-0" :style="{ zIndex }">
        <div class="fixed inset-0 bg-gray-900/60" aria-hidden="true"></div>

        <div
          class="fixed inset-0 overflow-y-auto"
          @mousedown.self="backdropPressed = true"
          @click.self="handleBackdropClick"
        >
          <div
            class="flex min-h-full items-center justify-center p-4 sm:p-6"
            @mousedown.self="backdropPressed = true"
            @click.self="handleBackdropClick"
          >
            <div
              ref="panelRef"
              role="dialog"
              aria-modal="true"
              :aria-labelledby="hasTitle ? titleId : undefined"
              :aria-label="!hasTitle && ariaLabel ? ariaLabel : undefined"
              tabindex="-1"
              :class="panelClasses"
              @focusin="rememberFocus"
            >
              <header
                v-if="hasTitle"
                class="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4"
              >
                <div class="min-w-0 flex-1">
                  <h2 :id="titleId" class="text-lg font-semibold leading-7 text-gray-900">
                    <slot name="title">{{ title }}</slot>
                  </h2>
                  <p v-if="description || $slots.description" class="mt-0.5 text-sm text-gray-600">
                    <slot name="description">{{ description }}</slot>
                  </p>
                </div>

                <button
                  v-if="closable"
                  type="button"
                  aria-label="Cerrar"
                  :disabled="persistent"
                  class="-mr-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                  @click="requestClose"
                >
                  <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </header>

              <div :class="['min-h-0 flex-1 overflow-y-auto', bodyClass]">
                <slot name="content">
                  <slot />
                </slot>
              </div>

              <footer
                v-if="$slots.footer"
                class="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3"
              >
                <slot name="footer" />
              </footer>

              <!-- Confirmación al cerrar con cambios sin guardar -->
              <div
                v-if="confirmingDiscard"
                class="absolute inset-0 z-10 flex items-center justify-center bg-white/90 p-6"
              >
                <div
                  ref="discardRef"
                  role="alertdialog"
                  aria-modal="true"
                  :aria-labelledby="`${titleId}-discard`"
                  :aria-describedby="`${titleId}-discard-msg`"
                  class="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-6 shadow-lg"
                >
                  <h3 :id="`${titleId}-discard`" class="text-base font-semibold text-gray-900">
                    {{ discardTitle }}
                  </h3>
                  <p :id="`${titleId}-discard-msg`" class="mt-2 text-sm text-gray-600">
                    {{ discardMessage }}
                  </p>
                  <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <BaseButton ref="keepEditingRef" variant="secondary" @click="keepEditing">
                      {{ discardCancelText }}
                    </BaseButton>
                    <BaseButton variant="danger" @click="confirmDiscard">
                      {{ discardConfirmText }}
                    </BaseButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
// Modal base del portal. Diálogo accesible (role, aria-modal, título), foco
// atrapado y devuelto al cerrar, Esc solo en el modal de arriba de la pila, pie
// fijo con los botones apilados en móvil y, con `dirty`, una confirmación antes
// de descartar cambios. Para que el "Cancelar" propio del modal pase por la
// misma confirmación, llamar a `requestClose()` (expuesto por ref).
import { computed, nextTick, onBeforeUnmount, ref, useId, useSlots, watch } from "vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import BaseButton from "./BaseButton.vue";
import { createModalId, isTopModal, modalZIndex, pushModal, removeModal } from "./modalStack";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  // Nombre accesible cuando el modal no tiene título visible
  ariaLabel: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["xs", "sm", "md", "lg", "xl", "2xl", "3xl", "full"].includes(value),
  },
  // Clases extra del panel, ej. un alto fijo para que el modal no cambie de tamaño entre pasos.
  panelClass: {
    type: String,
    default: "",
  },
  // Relleno del cuerpo; "p-0" para contenidos que ocupan todo el panel.
  bodyClass: {
    type: String,
    default: "px-6 py-5",
  },
  closable: {
    type: Boolean,
    default: true,
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
  closeOnEsc: {
    type: Boolean,
    default: true,
  },
  // Mientras algo se procesa: no se puede cerrar de ninguna forma.
  persistent: {
    type: Boolean,
    default: false,
  },
  // Hay cambios sin guardar: cerrar pide confirmación.
  dirty: {
    type: Boolean,
    default: false,
  },
  discardTitle: {
    type: String,
    default: "¿Descartar los cambios?",
  },
  discardMessage: {
    type: String,
    default: "Lo que llevas hecho en esta ventana se perderá.",
  },
  discardConfirmText: {
    type: String,
    default: "Descartar",
  },
  discardCancelText: {
    type: String,
    default: "Seguir editando",
  },
});

const emit = defineEmits(["close", "opened", "closed"]);
const slots = useSlots();

const modalId = createModalId();
const titleId = `${useId()}-title`;
const panelRef = ref(null);
const keepEditingRef = ref(null);
const discardRef = ref(null);
const confirmingDiscard = ref(false);
const backdropPressed = ref(false);
let previouslyFocused = null;
let focusBeforeDiscard = null;
// Último elemento con foco dentro del panel: un clic en el fondo saca el foco
// del campo antes de pedir cerrar, y "Seguir editando" debe devolverlo ahí.
let lastFocusedInPanel = null;

const rememberFocus = (event) => {
  if (!discardRef.value?.contains(event.target)) lastFocusedInPanel = event.target;
};

const hasTitle = computed(() => Boolean(props.title || slots.title));
const zIndex = computed(() => modalZIndex(modalId));

const SIZE_CLASSES = {
  xs: "max-w-xs",
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-6xl",
  "3xl": "max-w-7xl",
  full: "max-w-full",
};

const panelClasses = computed(() => [
  "relative flex w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl focus:outline-none",
  "max-h-[calc(100dvh-2rem)] sm:max-h-[90vh]",
  SIZE_CLASSES[props.size],
  props.panelClass,
]);

// Cierre pedido por la X, el fondo, Esc o el Cancelar del consumidor.
const requestClose = () => {
  if (props.persistent) return;
  if (props.dirty) {
    focusBeforeDiscard = panelRef.value?.contains(document.activeElement) ? document.activeElement : lastFocusedInPanel;
    confirmingDiscard.value = true;
    nextTick(() => keepEditingRef.value?.$el?.focus());
    return;
  }
  emit("close");
};

// Seguir editando: el foco vuelve a donde estaba antes de pedir cerrar.
const keepEditing = () => {
  confirmingDiscard.value = false;
  const target = focusBeforeDiscard;
  focusBeforeDiscard = null;
  nextTick(() => {
    const element = target?.isConnected && panelRef.value?.contains(target) ? target : panelRef.value;
    element?.focus({ preventScroll: true });
  });
};

const confirmDiscard = () => {
  confirmingDiscard.value = false;
  emit("close");
};

// Solo cuenta un clic que empezó y terminó en el fondo: arrastrar una selección
// de texto desde un campo hacia afuera no debe cerrar el modal.
const handleBackdropClick = () => {
  const startedOnBackdrop = backdropPressed.value;
  backdropPressed.value = false;
  if (!startedOnBackdrop || !props.closeOnBackdrop) return;
  requestClose();
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

// Con la confirmación de descarte abierta, el foco queda dentro de ella.
const trapScope = () => (confirmingDiscard.value && discardRef.value) || panelRef.value;

const focusableElements = () =>
  Array.from(trapScope()?.querySelectorAll(FOCUSABLE) || []).filter(
    (element) => element.offsetParent !== null || element === document.activeElement
  );

// Los paneles flotantes (BaseSelect, DatePicker…) viven en un Portal fuera del
// modal; el foco puede estar ahí sin que sea una fuga.
const isInsideAllowedArea = (element) =>
  Boolean(element) && (trapScope()?.contains(element) || element.closest?.("#headlessui-portal-root"));

const handleKeydown = (event) => {
  if (!isTopModal(modalId)) return;

  if (event.key === "Escape") {
    if (event.defaultPrevented) return;
    event.preventDefault();
    if (confirmingDiscard.value) {
      keepEditing();
      return;
    }
    if (props.closeOnEsc) requestClose();
    return;
  }

  if (event.key !== "Tab" || !panelRef.value) return;

  const elements = focusableElements();
  if (elements.length === 0) {
    event.preventDefault();
    panelRef.value.focus();
    return;
  }

  const first = elements[0];
  const last = elements[elements.length - 1];
  const active = document.activeElement;

  if (!isInsideAllowedArea(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && (active === first || active === trapScope())) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

const focusInitial = () => {
  const panel = panelRef.value;
  if (!panel) return;
  const preferred = panel.querySelector("[data-autofocus]");
  (preferred || panel).focus({ preventScroll: true });
};

const open = () => {
  previouslyFocused = document.activeElement;
  pushModal(modalId);
  document.addEventListener("keydown", handleKeydown);
  nextTick(focusInitial);
};

const close = () => {
  confirmingDiscard.value = false;
  lastFocusedInPanel = null;
  document.removeEventListener("keydown", handleKeydown);
  removeModal(modalId);
  if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
  previouslyFocused = null;
};

watch(
  () => props.show,
  (isOpen, wasOpen) => {
    if (isOpen) open();
    else if (wasOpen) close();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (props.show) close();
});

defineExpose({ requestClose });
</script>
